import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CreateCallInput,
  CreateNoteInput,
  CreateTaskInput,
  Task,
  TaskList,
  TaskStatus,
  UpdateNoteInput,
  UpdateTaskInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { customerKeys } from '../customers/use-customers.js';
import {
  completeTask,
  createNote,
  createTask,
  deleteNote,
  deleteTask,
  listCalls,
  listNotes,
  listTasks,
  logCall,
  setTaskStatus,
  updateNote,
  updateTask,
  type ActivityCall,
  type ActivityNote,
  type ActivitySubject,
  type TaskCompletion,
} from './activity-api.js';

export type { ActivityCall, ActivityNote, ActivitySubject } from './activity-api.js';

/**
 * A customer's notes, calls and follow-up tasks, as data.
 *
 * THE KEYS LIVE UNDER THE `customers` PREFIX, on purpose: a merge moves
 * a customer's notes, calls and tasks, and it already invalidates `customerKeys.all`, so a list
 * keyed anywhere else would go on showing rows that now belong to somebody else.
 *
 * WHAT EACH WRITE REFRESHES:
 * - a note or a call: its own list, and the timeline it appears on.
 * - a task: the whole record's prefix. A task write recomputes the record's `nextFollowUpAt` on
 *   the server, which changes the record itself, its place in the list, the follow-ups queue
 *   and the header bell (all under `customers`). Refreshing only the task list
 *   would leave the page showing a task due Thursday beside a header saying nothing is due.
 */
export const activityKeys = {
  notes: (subject: ActivitySubject) => ['customers', 'notes', subject.id] as const,
  calls: (subject: ActivitySubject) => ['customers', 'calls', subject.id] as const,
  tasks: (subject: ActivitySubject) => ['customers', 'tasks', subject.id] as const,
};

function timelineKey(subject: ActivitySubject): readonly unknown[] {
  return customerKeys.timeline(subject.id);
}

/** A subject whose id is not known yet — the page before its record loads. */
const isReady = (subject: ActivitySubject | null): subject is ActivitySubject =>
  subject !== null && subject.id !== '';

/* ----------------------------------------------------------------------------- Notes ------- */

export function useNotes(subject: ActivitySubject | null): UseQueryResult<{
  items: ActivityNote[];
}> {
  const client = useApiClient();
  return useQuery({
    queryKey: subject ? activityKeys.notes(subject) : ['notes', 'none'],
    queryFn: () => listNotes(client, subject as ActivitySubject),
    enabled: isReady(subject),
  });
}

/** Shared by every note and call write: the list itself, and the timeline it shows up on. */
function useListInvalidation(
  subject: ActivitySubject,
  key: (subject: ActivitySubject) => readonly unknown[],
): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: key(subject) });
    void queryClient.invalidateQueries({ queryKey: timelineKey(subject) });
  };
}

export function useCreateNote(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useListInvalidation(subject, activityKeys.notes);
  return useMutation<ActivityNote, unknown, CreateNoteInput>({
    mutationFn: (input) => createNote(client, subject, input),
    onSuccess: invalidate,
  });
}

export function useUpdateNote(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useListInvalidation(subject, activityKeys.notes);
  return useMutation<ActivityNote, unknown, { noteId: string; input: UpdateNoteInput }>({
    mutationFn: ({ noteId, input }) => updateNote(client, subject, noteId, input),
    onSuccess: invalidate,
  });
}

export function useDeleteNote(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useListInvalidation(subject, activityKeys.notes);
  return useMutation<void, unknown, string>({
    mutationFn: (noteId) => deleteNote(client, subject, noteId),
    onSuccess: invalidate,
  });
}

/* ----------------------------------------------------------------------------- Calls ------- */

export function useCalls(subject: ActivitySubject | null): UseQueryResult<{
  items: ActivityCall[];
}> {
  const client = useApiClient();
  return useQuery({
    queryKey: subject ? activityKeys.calls(subject) : ['calls', 'none'],
    queryFn: () => listCalls(client, subject as ActivitySubject),
    enabled: isReady(subject),
  });
}

export function useLogCall(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useListInvalidation(subject, activityKeys.calls);
  return useMutation<ActivityCall, unknown, CreateCallInput>({
    mutationFn: (input) => logCall(client, subject, input),
    onSuccess: invalidate,
  });
}

/* ----------------------------------------------------------------------------- Tasks ------- */

/** One customer's tasks. `null` while there is nobody to ask about. */
export function useTasks(subject: ActivitySubject | null): UseQueryResult<TaskList> {
  const client = useApiClient();
  return useQuery({
    queryKey: subject ? activityKeys.tasks(subject) : ['tasks', 'none'],
    queryFn: () => listTasks(client, subject as ActivitySubject),
    enabled: isReady(subject),
  });
}

/** Shared by every task write — see the note at the top of this file. */
function useTaskInvalidation(): () => void {
  const queryClient = useQueryClient();
  // The task list, the timeline, the customer's follow-up date and the follow-ups queue all live
  // under `customers`.
  return () => void queryClient.invalidateQueries({ queryKey: customerKeys.all });
}

export function useCreateTask(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useTaskInvalidation();
  return useMutation<Task, unknown, CreateTaskInput>({
    mutationFn: (input) => createTask(client, subject, input),
    onSuccess: invalidate,
  });
}

export function useUpdateTask(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useTaskInvalidation();
  return useMutation<Task, unknown, { taskId: string; input: UpdateTaskInput }>({
    mutationFn: ({ taskId, input }) => updateTask(client, subject, taskId, input),
    onSuccess: invalidate,
  });
}

export function useSetTaskStatus(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useTaskInvalidation();
  return useMutation<Task, unknown, { taskId: string; status: TaskStatus }>({
    mutationFn: ({ taskId, status }) => setTaskStatus(client, subject, taskId, status),
    onSuccess: invalidate,
  });
}

export function useDeleteTask(subject: ActivitySubject) {
  const client = useApiClient();
  const invalidate = useTaskInvalidation();
  return useMutation<void, unknown, string>({
    mutationFn: (taskId) => deleteTask(client, subject, taskId),
    onSuccess: invalidate,
  });
}

/**
 * Completing a task and, optionally, logging the call that completed it — one request, so the
 * screen can never show a done task beside a missing call. Refreshes the calls list too.
 */
export function useCompleteTask(subject: ActivitySubject) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useTaskInvalidation();
  return useMutation<TaskCompletion, unknown, { taskId: string; call?: CreateCallInput }>({
    mutationFn: ({ taskId, call }) => completeTask(client, subject, taskId, call),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: activityKeys.calls(subject) });
    },
  });
}
